import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { FlowDurationUnits, FlowKind, FlowVersionTarget } from 'flow-schema';
import type React from 'react';

import { Text, api, getQueryProps, isDefined } from 'common';

import { CallerPhoneNumberFormField } from './CallerPhoneNumberFormField';
import { PATIENT_REPLY_TIMEOUT_HELPER_TEXT, TIMEOUT_HELPER_TEXT } from './documentation';
import { FeatureFlagKey, useFlag } from '../../../../../../../utils/flags';
import { useFlowDefinitionContext } from '../../../../context';
import { BlockTagsFields } from '../../ConfigFields/BlockTagsFields';
import { BlockValueTemplateField } from '../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockDurationField } from '../../ConfigFields/Duration';
import { BlockSelectField } from '../../ConfigFields/SelectField';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';
import { SharedFlowArguments } from '../SubFlow/Shared/FlowArguments/SharedFlowArguments';
import { FlowVersionFormFields } from '../SubFlow/Shared/FlowVersion';

const TIMEOUT_LABEL = 'Timeout';
const PATIENT_REPLY_TIMEOUT_LABEL = 'Patient Reply Timeout';

export function CreateModeFormFields() {
  const { flowDefinition } = useFlowDefinitionContext();
  const { isEnabledOnBlock: isAssignmentGroupEnabled } = useFlag({
    flagKey: FeatureFlagKey.conversationTaskAssignmentGroup,
  });

  const { data: assignmentGroups = [] } = useQuery({
    ...getQueryProps(api.client.AssignmentGroup, 'findAll', {
      organizationId: flowDefinition.organizationId,
      limit: 500,
    }),
    enabled: isAssignmentGroupEnabled,
  });

  const fieldNamePrefix = 'config.patientReplyHandler';
  const { setValue: setPatientReplyHandler, value: patientReplyHandler } = useSelectedNodeField<
    | {
        versionTarget: FlowVersionTarget | undefined;
        flowDefinitionId: string | undefined;
        flowId: string | undefined;
        metadata: api.FlowDefinition.FlowArgument[] | undefined;
      }
    | undefined
  >(fieldNamePrefix);

  const patientReplyHandlerEnabled = !!patientReplyHandler;

  const { value: callerPhoneNumberId } = useSelectedNodeField<string | null>(
    'config.callerPhoneNumberId'
  );
  const { value: agentPhoneNumber } = useSelectedNodeField<string | undefined>(
    'config.agentPhoneNumber'
  );
  const isCustomPhoneNumber = !isDefined(callerPhoneNumberId) && isDefined(agentPhoneNumber);

  return (
    <Stack gap={2}>
      <CallerPhoneNumberFormField />
      {isCustomPhoneNumber && (
        <BlockValueTemplateField
          fieldName="config.agentPhoneNumber"
          label="Custom Sender Phone Number"
          helperText="Provide a valid phone number starting with country code (e.g. +1 for US)"
          placeholder="+1-###-###-####"
          required
        />
      )}
      <BlockValueTemplateField
        fieldName="config.firstMessage"
        label="First Message"
        helperText="The first message to send to the patient when starting the conversation"
        required
      />
      <Text variant="subtitle2" bold>
        ⚠️ HIPAA Security Notice
      </Text>
      <Text variant="subtitle2">
        SMS communications are not encrypted or secure. DO NOT configure workflows that request,
        collect, or transmit PHI via SMS. <br></br> PHI includes: patient names, medical conditions,
        symptoms, medications, appointment details with providers, or any health-related
        information.
      </Text>
      <BlockTagsFields />
      <BlockValueTemplateField
        fieldName="config.priority"
        label="Priority"
        helperText="Provide a priority for the conversation"
        placeholder="24 hour"
      />
      {isAssignmentGroupEnabled && (
        <BlockSelectField
          label="Assignment Group"
          fieldName="config.assignmentGroupId"
          fieldVariant="SelectOrBlockValueTemplate"
          options={assignmentGroups.map((assignmentGroup) => ({
            label: assignmentGroup.name,
            value: assignmentGroup.id,
          }))}
        />
      )}
      <BlockDurationField
        variant="BlockValueTemplate"
        fieldName="config.timeout"
        unitFieldNameSuffix="units"
        required={true}
        allowedUnits={[FlowDurationUnits.Minutes, FlowDurationUnits.Hours, FlowDurationUnits.Days]}
        toggleLabel={TIMEOUT_LABEL}
        valueLabel={TIMEOUT_LABEL}
        valueHelperText={TIMEOUT_HELPER_TEXT}
      />
      <BlockDurationField
        variant="BlockValueTemplate"
        fieldName="config.noPatientResponseTimeout"
        unitFieldNameSuffix="units"
        required={false}
        allowedUnits={[FlowDurationUnits.Minutes, FlowDurationUnits.Hours, FlowDurationUnits.Days]}
        toggleLabel={PATIENT_REPLY_TIMEOUT_LABEL}
        valueLabel={PATIENT_REPLY_TIMEOUT_LABEL}
        valueHelperText={PATIENT_REPLY_TIMEOUT_HELPER_TEXT}
      />
      <Divider sx={{ marginY: 1.5 }} />
      <FormControlLabel
        control={
          <Checkbox
            checked={patientReplyHandlerEnabled}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const checked = event.target.checked;
              if (!checked) {
                setPatientReplyHandler(undefined);
              } else {
                setPatientReplyHandler({
                  versionTarget: FlowVersionTarget.SpecificVersion,
                  flowDefinitionId: undefined,
                  flowId: undefined,
                  metadata: undefined,
                });
              }
            }}
          />
        }
        label="Add patient reply handler flow"
      />
      {patientReplyHandlerEnabled && (
        <FlowVersionFormFields
          fieldNamePrefix={fieldNamePrefix}
          variant="SubflowPicker"
          allowedFlowKinds={[FlowKind.Base]}
        >
          <Accordion defaultExpanded variant="elevation">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Text variant="heading5">Inputs</Text>
            </AccordionSummary>
            <AccordionDetails>
              <SharedFlowArguments fieldName={`${fieldNamePrefix}.metadata`} />
            </AccordionDetails>
          </Accordion>
        </FlowVersionFormFields>
      )}
    </Stack>
  );
}
